import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from '@ptg-shared/components';
import { VitalReportService } from '../../services/vital-report.service';
import { takeUntil } from 'rxjs/operators';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'ptg-vital-report-modal',
  templateUrl: './vital-report-modal.component.html',
  styleUrls: ['./vital-report-modal.component.scss'],
})
export class VitalReportModalComponent extends BaseComponent implements OnInit {
  formGroup: FormGroup;
  fromDate = new FormControl('', Validators.required);
  toDate = new FormControl('', Validators.required);
  isGenerating = false;
  pollInterval: any;

  constructor(
    public dialogRef: MatDialogRef<VitalReportModalComponent>,
    private vitalReportService: VitalReportService,
    private snackBar: MatSnackBar,
    private datePipe: DatePipe
  ) {
    super();

    this.formGroup = new FormGroup({
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
  }

  ngOnInit(): void {}

  onCancel(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.dialogRef.close();
  }

  onGenerate(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    // Validate that toDate is after fromDate
    const fromDateValue = this.fromDate.value;
    const toDateValue = this.toDate.value;

    if (fromDateValue && toDateValue) {
      const fromDateObj = new Date(fromDateValue);
      const toDateObj = new Date(toDateValue);

      if (fromDateObj > toDateObj) {
        this.snackBar.open('To Date must be after From Date', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar'],
        });
        return;
      }
    }

    this.isGenerating = true;

    // Convert dates to UTC
    const fromDateUTC = new Date(this.fromDate.value);
    fromDateUTC.setUTCHours(0, 0, 0, 0);
    
    const toDateUTC = new Date(this.toDate.value);
    toDateUTC.setUTCHours(23, 59, 59, 999);

    const request = {
      fromDate: fromDateUTC.toISOString(),
      toDate: toDateUTC.toISOString(),
    };

    this.vitalReportService
      .generateVitalRecordsExport(request)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.snackBar.open(
            'Vital report started processing. Once done, system will download it.',
            'Close',
            {
              duration: 5000,
              panelClass: ['success-snackbar'],
            }
          );

          // Start polling for the download
          this.startPolling(response.token);
        },
        error: (error) => {
          this.isGenerating = false;
          this.snackBar.open(
            'Error generating report. Please try again.',
            'Close',
            {
              duration: 5000,
              panelClass: ['error-snackbar'],
            }
          );
        },
      });
  }

  private startPolling(token: string): void {
    let attempts = 0;
    const maxAttempts = 300; // Poll for 5 minutes (300 attempts * 1 second)

    this.pollInterval = setInterval(() => {
      attempts++;

      if (attempts > maxAttempts) {
        clearInterval(this.pollInterval);
        this.isGenerating = false;
        this.snackBar.open(
          'Report generation is taking longer than expected. Please check back later.',
          'Close',
          {
            duration: 5000,
            panelClass: ['warning-snackbar'],
          }
        );
        this.dialogRef.close();
        return;
      }

      this.vitalReportService
        .downloadVitalRecordsExport(token)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (blob) => {
            if (blob && blob.size > 0) {
              // File is ready, download it
              clearInterval(this.pollInterval);
              this.downloadFile(blob);
              this.isGenerating = false;
              this.snackBar.open('Report downloaded successfully!', 'Close', {
                duration: 3000,
                panelClass: ['success-snackbar'],
              });
              this.dialogRef.close();
            }
          },
          error: (error) => {
            // 204 No Content means file is not ready yet, continue polling
            if (error.status !== 204) {
              // Actual error occurred
              clearInterval(this.pollInterval);
              this.isGenerating = false;
              this.snackBar.open(
                'Error downloading report. Please try again.',
                'Close',
                {
                  duration: 5000,
                  panelClass: ['error-snackbar'],
                }
              );
              this.dialogRef.close();
            }
            // If 204, continue polling
          },
        });
    }, 1000); // Poll every 1 second
  }

  private downloadFile(blob: Blob): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = this.datePipe.transform(new Date(), 'yyyyMMdd_HHmmss');
    link.download = `Vital_Records_Report_${timestamp}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  ngOnDestroy(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    super.ngOnDestroy();
  }
}
